import React, { useState, useEffect } from "react";
import { FaSync, FaBolt, FaEnvelope, FaDatabase, FaServer, FaNetworkWired, FaCheckCircle, FaExclamationTriangle, FaTimesCircle, FaArrowRight, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import TimeFilter from "../components/TimeFilter";
import InfoTooltip from "../components/InfoTooltip";
import ServiceDetailModal from "../components/ServiceDetailModal";
import LoadingOverlay from "../components/LoadingOverlay";
import { fetchSystemHealth, fetchServiceDetails } from "../services/healthService";
import { useTimeFilter } from "../context/TimeFilterContext";
import { getAvailablePlans } from "../services/analyticsService";
import { getNodeTitle } from "../utils/nodeMappings";
/**
 * @file Health.js
 * @description System Health dashboard showing uptime, error rates, and infrastructure metrics.
 */
export default function Health() {
    const [loading, setLoading] = useState(true);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [data, setData] = useState(null);

    // Detail Modal State
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [selectedServiceData, setSelectedServiceData] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);

    // Time Filter State

    const { timeFilter, setTimeFilter, customRange, handleCustomRangeChange, planFilter, setPlanFilter, availablePlans, setAvailablePlans } = useTimeFilter();

    // Pagination State
    const [ec2Page, setEc2Page] = useState(1);
    const [corePage, setCorePage] = useState(1);
    const itemsPerPage = 5;


    const loadData = (force = false) => {
        // Guard Clause
        if (timeFilter === 'custom' && (!customRange?.start || !customRange?.end)) return;
        if (timeFilter === 'custom' && new Date(customRange.start) > new Date(customRange.end)) return;

        setLoading(true);
        // Updated signature: timePeriod, planFilter, customRange, forceRefresh
        fetchSystemHealth(timeFilter, planFilter, customRange, force).then((res) => {
            if (res) {
                setData(res);
                setLastUpdated(new Date());

                // Delay to allow charts to render
                setTimeout(() => {
                    setLoading(false);
                }, 600);
            }
        });
    };

    const handleViewDetails = async (serviceName) => {
        setShowDetailModal(true);
        setSelectedServiceData(null);
        setDetailLoading(true);

        try {
            const details = await fetchServiceDetails(serviceName, timeFilter);
            setSelectedServiceData(details);
        } catch (error) {
            console.error("Failed to load service details", error);
            // Could set an error state here to show in modal
        } finally {
            setDetailLoading(false);
        }
    };

    useEffect(() => {
        if (timeFilter === 'custom' && (!customRange?.start || !customRange?.end)) return;
        if (timeFilter === 'custom' && new Date(customRange.start) > new Date(customRange.end)) return;
        getAvailablePlans(timeFilter, customRange).then(setAvailablePlans);
    }, [timeFilter, customRange]);

    useEffect(() => {
        // Guard already in loadData, but good to have here too implicitly via loadData call
        // Added customRange trigger
        loadData();
    }, [timeFilter, planFilter, customRange]);

    if (!data) {
        return (
            <div className="min-vh-100 position-relative bg-light">
                <LoadingOverlay loading={true} message="Loading System Health..." />
            </div>
        );
    }

    // --- UTILS ---
    const getStatusIcon = (status) => {
        const s = (status || "").toLowerCase();
        if (s === "operational" || s === "healthy" || s === "success") return <FaCheckCircle className="text-success" />;
        if (s === "degraded" || s === "warning") return <FaExclamationTriangle className="text-warning" />;
        if (s === "outage" || s === "error" || s === "danger" || s === "timeout") return <FaTimesCircle className="text-danger" />;
        return <FaCheckCircle className="text-muted" />;
    };

    const getStatusColor = (status) => {
        const s = (status || "").toLowerCase();
        if (s === "operational" || s === "healthy" || s === "success") return "success";
        if (s === "degraded" || s === "warning") return "warning";
        return "danger";
    };

    const getIcon = (iconType) => {
        switch (iconType) {
            case "FaBolt": return FaBolt;
            case "FaEnvelope": return FaEnvelope;
            case "FaDatabase": return FaDatabase;
            case "FaServer": return FaServer;
            case "FaNetworkWired": return FaNetworkWired;
            default: return FaServer;
        }
    };

    const kpis = data.summary_kpis || {};
    const infra = data.shared_infrastructure || {};
    const ec2 = data.ec2_instances || [];
    const coreServices = data.core_platform_services || [];
    const dependencies = data.external_dependencies || [];
    const queues = data.sqs_queues || [];

    return (
        <div className="p-4 bg-light min-vh-100 position-relative">
            <LoadingOverlay loading={loading} />
            <ServiceDetailModal
                show={showDetailModal}
                data={selectedServiceData}
                loading={detailLoading}
                onClose={() => setShowDetailModal(false)}
            />

            {/* HEADER */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h4 className="fw-bold mb-1">System Health</h4>
                    <p className="text-muted small mb-0">Monitor platform stability, dependencies, and infrastructure.</p>
                </div>
                <div className="d-flex align-items-center gap-3">
                    {availablePlans.length > 0 && (
                        <select
                            className="form-select form-select-sm shadow-sm border-secondary-subtle"
                            style={{ width: "150px", fontWeight: "500" }}
                            value={planFilter || ""}
                            onChange={(e) => setPlanFilter(e.target.value || null)}
                        >
                            <option value="">All Plans</option>
                            {availablePlans.map((p) => (
                                <option key={p} value={p}>
                                    {p.charAt(0).toUpperCase() + p.slice(1)}
                                </option>
                            ))}
                        </select>
                    )}
                    <TimeFilter
                        value={timeFilter}
                        onChange={setTimeFilter}
                        startDate={customRange.start}
                        endDate={customRange.end}
                        onCustomRangeChange={handleCustomRangeChange}
                    />
                    <div className="text-end d-none d-md-block ms-2">
                        <div className="text-muted" style={{ fontSize: "0.75rem" }}>Last Update</div>
                        <div className="fw-bold small">{lastUpdated ? lastUpdated.toLocaleTimeString() : (data.last_updated ? new Date(data.last_updated).toLocaleTimeString() : "-")}</div>
                    </div>
                    <button className="btn btn-white border shadow-sm btn-sm px-3" onClick={() => loadData(true)} disabled={loading}>
                        <FaSync className={loading ? "fa-spin me-2" : "me-2"} /> Refresh
                    </button>
                </div>
            </div>

            {/* 1. KPI STRIP */}
            <div className="row g-3 mb-4">
                {[
                    { label: "Availability", val: kpis.availability, status: "success", tip: "Globally available uptime." }, // Assuming 'availability' is good
                    { label: "Error Rate", val: kpis.error_rate, status: kpis.error_rate === "0.0%" ? "success" : "danger", tip: "Global error for core platform services currently." },
                    { label: "Avg Latency", val: kpis.avg_latency, status: "warning", tip: "Global average latency." },
                    { label: "Active Incidents", val: kpis.active_incidents, status: kpis.active_incidents === 0 ? "success" : "danger", tip: "Current active incidents." },
                ].map((k, i) => (
                    <div key={i} className="col-xl-3 col-md-6">
                        <div className={`card border-0 shadow-sm p-3 h-100 border-top border-4 border-${k.status}`}>
                            <div className="d-flex justify-content-between align-items-start">
                                <div>
                                    <div className="text-uppercase text-muted small fw-bold mb-1">
                                        {k.label} <InfoTooltip text={k.tip} />
                                    </div>
                                    <h3 className="fw-bold mb-0">{k.val}</h3>
                                </div>
                                <div className={`text-${k.status}`}>
                                    {k.status === 'success' ? <FaCheckCircle size={24} /> : k.status === 'warning' ? <FaExclamationTriangle size={24} /> : <FaTimesCircle size={24} />}
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <div className="row g-4 mb-4">
                {/* 2. INFRASTRUCTURE & EC2 */}
                <div className="col-lg-8">
                    {/* EC2 INSTANCES */}
                    <div className="card border-0 shadow-sm rounded-3 mb-4">
                        <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center">
                            <h6 className="fw-bold mb-0">EC2 Instances <InfoTooltip text="Health of backend nodes." /></h6>
                            <div className="d-flex align-items-center gap-1 bg-light rounded px-2 py-1">
                                <button className="btn btn-sm btn-link text-dark p-0 text-decoration-none" onClick={() => setEc2Page(prev => Math.max(prev - 1, 1))} disabled={ec2Page === 1} style={{ fontSize: '1.2rem', lineHeight: '1' }}>
                                    <FaChevronLeft size={12} />
                                </button>
                                <span className="small text-muted mx-2" style={{ fontSize: '0.8rem', minWidth: '40px', textAlign: 'center' }}>
                                    {ec2Page} / {Math.ceil(ec2.length / itemsPerPage) || 1}
                                </span>
                                <button className="btn btn-sm btn-link text-dark p-0 text-decoration-none" onClick={() => setEc2Page(prev => Math.min(prev + 1, Math.ceil(ec2.length / itemsPerPage)))} disabled={ec2Page >= Math.ceil(ec2.length / itemsPerPage)} style={{ fontSize: '1.2rem', lineHeight: '1' }}>
                                    <FaChevronRight size={12} />
                                </button>
                            </div>
                        </div>
                        <div className="table-responsive">
                            <table className="table table-hover align-middle mb-0 text-sm">
                                <thead className="bg-light">
                                    <tr className="small text-muted text-uppercase">
                                        <th className="ps-4">Name</th>
                                        <th>Instance ID</th>
                                        <th>Type</th>
                                        <th>CPU</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {ec2.slice((ec2Page - 1) * itemsPerPage, ec2Page * itemsPerPage).map((d, i) => (
                                        <tr key={i}>
                                            <td className="ps-4 text-dark">{d.name}</td>
                                            <td className="font-monospace text-muted">{d.instance_id}</td>
                                            <td><span className="badge bg-light text-dark border">{d.instance_type}</span></td>
                                            <td>
                                                <div className="d-flex align-items-center gap-2">
                                                    <div className="progress flex-grow-1" style={{ height: "6px", width: "60px" }}>
                                                        <div className="progress-bar bg-primary" style={{ width: `${d.metrics.cpu_utilization}%` }}></div>
                                                    </div>
                                                    <span className="small text-muted">{d.metrics.cpu_utilization}%</span>
                                                </div>
                                            </td>
                                            <td>{getStatusIcon(d.status)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* EXTERNAL DEPENDENCIES */}
                    <div className="card border-0 shadow-sm rounded-3">
                        <div className="card-header bg-white py-3">
                            <h6 className="fw-bold mb-0">External Dependencies <InfoTooltip text="Status of external API providers." /></h6>
                        </div>
                        <div className="card-body">
                            <div className="row g-3">
                                {dependencies.map((d, i) => {
                                    // Use generic icons based on service name or just FaServer
                                    let Icon = FaServer;
                                    if (d.service.includes("Milvus") || d.service.includes("MySQL")) Icon = FaDatabase;

                                    const statusColor = getStatusColor(d.status);

                                    return (
                                        <div className="col-md-6" key={i}>
                                            <div className={`p-3 border rounded border-start border-4 border-${statusColor} bg-white shadow-sm h-100`}>
                                                <div className="d-flex align-items-center justify-content-between mb-2">
                                                    <div className="d-flex align-items-center gap-2">
                                                        <Icon className="text-secondary" />
                                                        <span className="fw-bold text-dark">{getNodeTitle(d.service)}</span>
                                                    </div>
                                                    {getStatusIcon(d.status)}
                                                </div>
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <span className={`badge bg-${statusColor}-subtle text-${statusColor} small text-uppercase`}>{d.status}</span>
                                                    <small className="text-muted">{d.latency_ms ? `${d.latency_ms.toFixed(2)}ms` : (d.error ? "Connection Error" : "OK")}</small>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>

                {/* 3. SHARED INFRA STATS (SIDEBAR) */}
                <div className="col-lg-4">
                    <div className="card border-0 shadow-sm rounded-3 h-100">
                        <div className="card-header bg-white py-3">
                            <h6 className="fw-bold mb-0">Shared Infrastructure <InfoTooltip text="Aggregate metrics." /></h6>
                        </div>
                        <div className="card-body">
                            <div className="mb-4">
                                <div className="d-flex justify-content-between mb-1">
                                    <span className="small fw-bold">CPU Saturation</span>
                                    <span className="small fw-bold">{infra.cpu_saturation}%</span>
                                </div>
                                <div className="progress" style={{ height: "8px" }}>
                                    <div className="progress-bar bg-primary" style={{ width: `${Math.min(infra.cpu_saturation, 100)}%` }}></div>
                                </div>
                            </div>
                            <div>
                                <div className="d-flex justify-content-between mb-1">
                                    <span className="small fw-bold">Memory Usage</span>
                                    <span className="small fw-bold">{infra.memory_usage}%</span>
                                </div>
                                <div className="progress" style={{ height: "8px" }}>
                                    <div className="progress-bar bg-purple" style={{ width: `${Math.min(infra.memory_usage, 100)}%`, backgroundColor: "#8b5cf6" }}></div>
                                </div>
                            </div>
                            <hr className="my-4" />
                            <div className="mb-3">
                                <h6 className="fw-bold small text-muted text-uppercase mb-3">SQS Queues</h6>
                                {queues.map((q, i) => (
                                    <div key={i} className="d-flex justify-content-between align-items-center mb-2 p-2 bg-light rounded">
                                        <div className="d-flex align-items-center gap-2">
                                            <FaNetworkWired className="text-muted" size={12} />
                                            <span className="small fw-bold">{q.queue_name}</span>
                                        </div>
                                        <span className="badge bg-secondary text-white">{q.backlog}</span>
                                    </div>
                                ))}
                            </div>
                            <hr className="my-4" />
                            <div className="d-flex justify-content-between align-items-center">
                                <div className="text-muted small fw-bold">Job Failures</div>
                                <h5 className="fw-bold mb-0 text-danger">{infra.job_failures}</h5>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* 4. CORE SERVICES ROW */}
            <div className="card border-0 shadow-sm rounded-3 mb-4">
                <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center">
                    <h6 className="fw-bold mb-0">Core Platform Services <InfoTooltip text="Shared internal services." /></h6>
                    <div className="d-flex align-items-center gap-1 bg-light rounded px-2 py-1">
                        <button className="btn btn-sm btn-link text-dark p-0 text-decoration-none" onClick={() => setCorePage(prev => Math.max(prev - 1, 1))} disabled={corePage === 1} style={{ fontSize: '1.2rem', lineHeight: '1' }}>
                            <FaChevronLeft size={12} />
                        </button>
                        <span className="small text-muted mx-2" style={{ fontSize: '0.8rem', minWidth: '40px', textAlign: 'center' }}>
                            {corePage} / {Math.ceil(coreServices.length / itemsPerPage) || 1}
                        </span>
                        <button className="btn btn-sm btn-link text-dark p-0 text-decoration-none" onClick={() => setCorePage(prev => Math.min(prev + 1, Math.ceil(coreServices.length / itemsPerPage)))} disabled={corePage >= Math.ceil(coreServices.length / itemsPerPage)} style={{ fontSize: '1.2rem', lineHeight: '1' }}>
                            <FaChevronRight size={12} />
                        </button>
                    </div>
                </div>
                <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0 text-sm">
                        <thead className="bg-light">
                            <tr className="small text-muted text-uppercase">
                                <th className="ps-4">Service</th>
                                <th>Status</th>
                                <th>Error Details</th>
                                <th className="pe-4 text-end">Latency</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {coreServices.slice((corePage - 1) * itemsPerPage, corePage * itemsPerPage).map((s, i) => {
                                const statusColor = getStatusColor(s.status);
                                return (
                                    <tr key={i}>
                                        <td className="ps-4">{getNodeTitle(s.service)}</td>
                                        <td>
                                            <span className={`badge bg-${statusColor}-subtle text-${statusColor} border border-${statusColor}-subtle rounded-pill text-uppercase`}>
                                                {s.status}
                                            </span>
                                        </td>
                                        <td className="text-muted small text-wrap" style={{ maxWidth: "400px" }}>
                                            {s.error || "-"}
                                        </td>
                                        <td className="pe-4 text-end text-muted font-monospace">{s.latency_ms > 0 ? `${s.latency_ms}ms` : "-"}</td>
                                        <td className="text-end pe-4 text-primary">
                                            <FaArrowRight size={12} />
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

        </div>
    );
}
